/*
 * PDP colour picker.
 *
 * Every swatch is a link to a sibling product. Instead of a full navigation
 * we fetch the sibling's main-product section and let Dawn's <product-info>
 * swap itself in place (the same code path Dawn uses for combined listings).
 * Sibling sections are prefetched on idle so the swap is usually instant.
 *
 * Anything that goes wrong falls back to a normal navigation.
 */
(function () {
  if (window.macadeAltPicker) return;
  window.macadeAltPicker = true;

  const LINK = '.alternative-product';
  const CACHE_TTL = 5 * 60 * 1000;
  const MAX_PREFETCH = 12;
  const cache = new Map(); // url -> { at, promise }
  let inFlight = null;

  const designMode = () => !!(window.Shopify && window.Shopify.designMode);
  const productInfo = () =>
    document.querySelector("product-info[id^='MainProduct']");
  const pathOf = (url) => new URL(url, window.location.origin).pathname;

  function sectionUrl(url, info) {
    const joiner = url.includes('?') ? '&' : '?';
    return `${url}${joiner}section_id=${info.dataset.section}`;
  }

  function fetchSection(url) {
    const hit = cache.get(url);
    if (hit && Date.now() - hit.at < CACHE_TTL) return hit.promise;

    const info = productInfo();
    if (!info) return Promise.reject(new Error('no product-info'));

    const promise = fetch(sectionUrl(url, info)).then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.text();
    });
    promise.catch(() => cache.delete(url));
    cache.set(url, { at: Date.now(), promise });
    return promise;
  }

  function siblingUrls(root) {
    return Array.from(root.querySelectorAll(LINK))
      .filter((link) => !link.hasAttribute('aria-current'))
      .map((link) => link.getAttribute('href'))
      .filter(Boolean);
  }

  function prefetchSiblings() {
    const info = productInfo();
    if (!info) return;
    // One at a time so we don't compete with the page's own requests.
    siblingUrls(info)
      .slice(0, MAX_PREFETCH)
      .reduce(
        (chain, url) => chain.then(() => fetchSection(url).catch(() => {})),
        Promise.resolve()
      );
  }

  function schedulePrefetch() {
    if (designMode()) return;
    if (navigator.connection && navigator.connection.saveData) return;
    const idle =
      window.requestIdleCallback || ((cb) => window.setTimeout(cb, 1500));
    const go = () => idle(prefetchSiblings, { timeout: 4000 });
    if (document.readyState === 'complete') go();
    else window.addEventListener('load', go, { once: true });
  }

  function recordHistory(url) {
    // Mirrors macade-history-recorder.js, which only runs on real page loads.
    try {
      const path = JSON.parse(localStorage.getItem('breadcrumbPath') || '[]');
      path.push(pathOf(url));
      localStorage.setItem('breadcrumbPath', JSON.stringify(path.slice(-500)));
    } catch (_) {
      /* storage unavailable */
    }
  }

  function updateDocumentMeta(oldInfo, newInfo, url) {
    const oldTitle = oldInfo.querySelector('.product__title h1');
    const newTitle = newInfo.querySelector('.product__title h1');
    if (oldTitle && newTitle) {
      const from = oldTitle.textContent.trim();
      const to = newTitle.textContent.trim();
      if (from && to && document.title.includes(from)) {
        document.title = document.title.replace(from, to);
      }
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.href = new URL(url, window.location.origin).href;
  }

  // Keep the swatch row where the shopper sees it. Dawn inserts the new
  // section and hides the old one, which defeats the browser's own scroll
  // anchoring, and async content (Yotpo stars, Voyado form, Kiwi chart, Shop
  // Pay installments) can shift things for a moment afterwards.
  //
  // On desktop the info column is sticky, which gives three situations:
  // - in flow (page top): the row moves with scroll, scroll by its offset;
  // - pinned at its sticky top: scroll cannot move the row at all, so a
  //   shift there (e.g. a longer title wrapping) is simply accepted;
  // - pushed up by the bottom of the product block (deep in the gallery):
  //   the row follows the block's bottom edge, so restore that state.
  const swatchGrid = (root) => root.querySelector(LINK)?.closest('.grid');
  const productBlock = (root) => root.querySelector('.product');
  const infoColumn = (root) => root.querySelector('.product__info-container');

  // Distance between the column's bottom and the product block's bottom.
  // ~0 means the column is being pushed up by the block.
  const columnSlack = (block, column) =>
    block && column
      ? block.getBoundingClientRect().bottom -
        column.getBoundingClientRect().bottom
      : Infinity;

  function measureAnchor(info, trigger) {
    const grid = (trigger && trigger.closest('.grid')) || swatchGrid(info);
    if (!grid) return null;
    return {
      rowTop: grid.getBoundingClientRect().top,
      pushed: columnSlack(productBlock(info), infoColumn(info)) < 1,
    };
  }

  let stopActivePin = null;

  function keepAnchorInPlace(info, anchor) {
    if (stopActivePin) stopActivePin();
    const grid = swatchGrid(info);
    if (!grid) return;
    const block = productBlock(info);
    const column = infoColumn(info);
    const stickyTop =
      column && getComputedStyle(column).position === 'sticky'
        ? parseFloat(getComputedStyle(column).top) || 0
        : null;

    const html = document.documentElement;
    const INPUT = ['wheel', 'touchstart', 'keydown'];
    const FRAME_WINDOW = 700; // check every frame this long
    const TOTAL_WINDOW = 4000; // then only on DOM/size changes, this long
    const MAX_ATTEMPTS = 40; // loop guard
    let attempts = 0;
    let stopped = false;
    let frame = 0;

    const scrollNow = (fn) => {
      const previous = html.style.scrollBehavior;
      html.style.scrollBehavior = 'auto';
      fn();
      html.style.scrollBehavior = previous;
    };
    const rowDelta = () => grid.getBoundingClientRect().top - anchor.rowTop;
    const pinned = () =>
      stickyTop !== null &&
      column &&
      Math.abs(column.getBoundingClientRect().top - stickyTop) < 1 &&
      columnSlack(block, column) >= 1;

    const correct = () => {
      if (stopped) return;
      if (!grid.isConnected) {
        stop();
        return;
      }
      const delta = rowDelta();
      if (Math.abs(delta) < 1) return;
      // Pinned before and after: the shift is inside the column and no
      // amount of scrolling changes it. Leave it.
      if (!anchor.pushed && pinned()) return;
      if (++attempts > MAX_ATTEMPTS) {
        stop();
        return;
      }

      const startY = window.scrollY;
      scrollNow(() => window.scrollBy(0, delta));
      const remaining = rowDelta();
      if (Math.abs(remaining) < 1) return;
      if (Math.abs(remaining) < Math.abs(delta) - 0.5) return; // moved as far as it can

      // Nothing moved: the column is pinned. If the shopper had it pushed up
      // by the block's bottom (deep in the gallery) and the row is now too
      // low, scroll down until the block pushes it up to where it was.
      scrollNow(() => window.scrollTo(0, startY));
      if (anchor.pushed && delta > 0) {
        const slack = columnSlack(block, column);
        if (slack > 0 && slack !== Infinity) {
          scrollNow(() => window.scrollBy(0, slack + delta));
        }
      }
      if (Math.abs(rowDelta()) >= 1) stop(); // can't be done; don't fight it
    };
    const stop = () => {
      stopped = true;
      if (stopActivePin === stop) stopActivePin = null;
      mutations.disconnect();
      sizes.disconnect();
      cancelAnimationFrame(frame);
      clearTimeout(timer);
      INPUT.forEach((type) => window.removeEventListener(type, stop));
    };
    stopActivePin = stop;

    // Content that arrives after the swap changes the DOM; the observer
    // callback runs before the next paint, so the correction is never
    // visible. Size changes without DOM changes are caught by the
    // ResizeObserver, and the first frames are checked unconditionally. Let
    // go as soon as the shopper scrolls themselves.
    const mutations = new MutationObserver(correct);
    mutations.observe(info, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    });
    // Dawn removes the hidden old section ~500ms after the swap.
    if (info.parentElement) {
      mutations.observe(info.parentElement, { childList: true });
    }
    mutations.observe(document.head, { childList: true, subtree: true });
    // The section's own height is set by the taller column, so also watch
    // the info column and the product block. The Shop Pay installments line
    // renders late inside a shadow root (invisible to the MutationObserver)
    // and only changes the column's height.
    const sizes = new ResizeObserver(correct);
    sizes.observe(info);
    if (block) sizes.observe(block);
    if (column) sizes.observe(column);
    const started = performance.now();
    const tick = () => {
      if (stopped) return;
      correct();
      if (performance.now() - started < FRAME_WINDOW) {
        frame = requestAnimationFrame(tick);
      }
    };
    const timer = setTimeout(stop, TOTAL_WINDOW);
    INPUT.forEach((type) =>
      window.addEventListener(type, stop, { passive: true })
    );

    correct();
    frame = requestAnimationFrame(tick);
  }

  // App bundles Shopify loads once in <head> that mount into markup inside
  // <product-info> and therefore have to run again after a swap. Matched on
  // filename so the extension version in the URL doesn't matter.
  //
  // - Voyado "notify me when back in stock": a React app that mounts into
  //   #voyado_app_block_bis and listens to the product form's variant input.
  //   Dawn has already renamed the ids in the old block, so a re-run finds
  //   the new block and the new form.
  const REMOUNT_SCRIPTS = [
    { file: 'app-block-bis-index', needs: '#voyado_app_block_bis' },
  ];

  function remountAppScripts(info) {
    REMOUNT_SCRIPTS.forEach(({ file, needs }) => {
      if (!info.querySelector(needs)) return;
      const original = document.querySelector(`head script[src*="${file}"]`);
      if (!original) return;
      const script = document.createElement('script');
      script.src = original.src;
      script.async = true;
      script.dataset.macadeRemount = file;
      script.onload = script.onerror = () => script.remove();
      document.head.appendChild(script);
    });
  }

  function afterSwap(url, anchor) {
    const info = productInfo();
    if (!info) return;

    remountAppScripts(info);

    if (
      info.hasAttribute('data-zoom-on-hover') &&
      typeof enableZoomOnHover === 'function'
    ) {
      enableZoomOnHover(2);
    }
    if (
      window.yotpoWidgetsContainer &&
      typeof window.yotpoWidgetsContainer.initWidgets === 'function'
    ) {
      window.yotpoWidgetsContainer.initWidgets();
    }

    const current = info.querySelector(`${LINK}[aria-current]`);
    if (current) current.focus({ preventScroll: true });

    if (anchor) keepAnchorInPlace(info, anchor);

    document.dispatchEvent(
      new CustomEvent('macade:product-swapped', {
        bubbles: true,
        detail: { url },
      })
    );
    schedulePrefetch();
  }

  function swapTo(url, { push = true, trigger = null } = {}) {
    const info = productInfo();
    if (!info || typeof info.handleSwapProduct !== 'function') {
      window.location.href = url;
      return;
    }
    if (pathOf(info.dataset.url) === pathOf(url)) return;

    inFlight = url;
    info.setAttribute('aria-busy', 'true');
    if (trigger) trigger.classList.add('alternative-product--loading');

    fetchSection(url)
      .then((text) => {
        if (inFlight !== url) return; // a later click superseded this one

        const html = new DOMParser().parseFromString(text, 'text/html');
        const next = html.querySelector('product-info');
        if (!next) throw new Error('response has no <product-info>');

        // Every script the section needs is already loaded on this page.
        // Dawn re-injects <script> tags when swapping, so drop the external
        // ones; inline data scripts (variant JSON, app data) stay.
        next.querySelectorAll('script[src]').forEach((s) => s.remove());

        if (stopActivePin) stopActivePin();
        const anchor = measureAnchor(info, trigger);

        if (push) history.pushState({ macadeProductUrl: url }, '', url);
        updateDocumentMeta(info, next, url);
        info.handleSwapProduct(url, false)(html);
        // Dawn's updateURL replaceState()s the entry with ?variant= and an
        // empty state; put our marker back on it.
        history.replaceState({ macadeProductUrl: url }, '', location.href);

        recordHistory(url);
        afterSwap(url, anchor);
      })
      .catch((error) => {
        console.error(
          '[macade] product swap failed, navigating instead',
          error
        );
        window.location.href = url;
      })
      .finally(() => {
        if (inFlight === url) inFlight = null;
        info.removeAttribute('aria-busy');
        if (trigger) trigger.classList.remove('alternative-product--loading');
      });
  }

  // Click: swap in place. Modifier clicks and middle clicks keep native behaviour.
  document.addEventListener('click', (event) => {
    const link = event.target.closest(LINK);
    if (!link || designMode()) return;
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    const url = link.getAttribute('href');
    if (!url || !link.closest('product-info')) return;

    event.preventDefault();
    if (link.hasAttribute('aria-current')) return;
    swapTo(url, { trigger: link });
  });

  // Hover / touch / focus: warm the cache for swatches not prefetched yet.
  const warm = (event) => {
    const link = event.target.closest(LINK);
    if (!link || link.hasAttribute('aria-current') || designMode()) return;
    const url = link.getAttribute('href');
    if (url) fetchSection(url).catch(() => {});
  };
  document.addEventListener('mouseover', warm, { passive: true });
  document.addEventListener('touchstart', warm, { passive: true });
  document.addEventListener('focusin', warm);

  // Show the hovered swatch's colour name next to "Color:".
  document.addEventListener('mouseover', (event) => {
    const img = event.target.closest(`${LINK} img`);
    if (!img) return;
    const scope = img.closest('product-info') || document;
    const label = scope.querySelector('.color-alt');
    if (label) label.textContent = img.dataset.color || '';
  });

  // Back / forward between swapped products.
  window.addEventListener('popstate', () => {
    const info = productInfo();
    if (!info) return;
    if (pathOf(info.dataset.url) !== window.location.pathname) {
      swapTo(window.location.pathname, { push: false });
    }
  });

  schedulePrefetch();
})();
